import { AllSameKeyValuesEnforcer, getSet } from "../helpers/helpers";
import { Exhibitor } from "./exhibitors.data";
import { MediaTermState, Performer } from "./performers.data";
import { Sponsor } from "./sponsors.data";
import { JoiComment } from "./tasks.data";
import { jsonCopyObject } from './../helpers/helpers';
import { Asset } from "./assets.data";
import { JoiResource } from "./resources.data";

import { Dictionary } from "./app.data";
import { CustomAnswer, CustomQuestion, JoiEvent, MediaTerms, Ranking } from "./events.data";
import { CoreEntityType } from "./share.data";
import { Attendee } from "./attendees.data";


export interface TemplateBase {
  versionNumber?: number // template and entity
  templateId?: string // template and entity
  eventId?: string // template only
  eventName?: string // template only
  dateAdded?: number // template and entity, refers to when added to or from template for entity
  dateModified?: number // template and entity, refers to when modified to or from template for entity
}

export interface EntityBase extends TemplateBase { 
  confirmed?: boolean
  contactName?: string
  contactFullAddress?: string
  fullLogoUrl?: string
  hideInPublish?: boolean
  hideLabels?: boolean
  id?: string
  labels?: string[]
  logoUrl?: string
  name?: string
  quickNotes?: JoiComment[]
  social?: SocialLink[]
  customQuestions?: Dictionary<CustomAnswer>
  entityTypeId?: string
  mediaTermsMap?:  Dictionary<MediaTermState>
  noTermsMap?: Dictionary<boolean>
  submitTemplateId?: string
  stars?: number
}

const PUBLIC_ENTITY_KEYS: (keyof EntityBase)[] = ['entityTypeId', 'mediaTermsMap', 'customQuestions', 'noTermsMap', 'labels', 'logoUrl', 'name', 'id', 'social', 'stars']
const EXCLUDED_ENTITY_TEMPLATE_KEYS: (keyof EntityBase)[] = ['mediaTermsMap', 'noTermsMap',
'confirmed', 'hideInPublish', 'hideLabels', 'quickNotes', 'labels', 'customQuestions', 'entityTypeId', 'mediaTermsMap', 'noTermsMap' ]

export const addPublicEntityKeys = <E extends EntityBase>(keys: Set<keyof E>) => PUBLIC_ENTITY_KEYS.forEach(k => keys.add(k))
export const addExcludedEntityTemplateKeys = <E extends EntityBase>(keys: Set<keyof E>) => EXCLUDED_ENTITY_TEMPLATE_KEYS.forEach(k => keys.add(k))

export interface DeprecatedEntityProps {
  noMediaTermsRequired?: boolean
  mediaTerms?: MediaTermState
}

export interface EntityBaseWithDeprecations extends EntityBase, DeprecatedEntityProps {}

export const entityProps: AllSameKeyValuesEnforcer<EntityBase> = {
  confirmed: 'confirmed', fullLogoUrl: 'fullLogoUrl', contactName: 'contactName', hideInPublish: 'hideInPublish', hideLabels: 'hideLabels', 
  id: 'id', labels: 'labels', logoUrl: 'logoUrl', quickNotes: 'quickNotes', social: 'social', versionNumber: 'versionNumber',
  templateId: 'templateId', eventId: 'eventId', eventName: 'eventName', dateAdded: 'dateAdded', dateModified: 'dateModified', name: 'name',
  contactFullAddress: 'contactFullAddress', customQuestions: 'customQuestions', entityTypeId: 'entityTypeId', mediaTermsMap: 'mediaTermsMap',
  noTermsMap: 'noTermsMap', submitTemplateId: 'submitTemplateId', stars: 'stars'
} 

export type Entity = Performer | Sponsor | Exhibitor | Asset | JoiResource | Attendee
export type TypedEntity = Entity & { type: 'performer' | 'sponsor' | 'exhibitor' | 'asset' | 'resource' | 'attendee' }

export const templatePropsToRemove: (keyof EntityBase)[] = ['eventId', 'eventName', 'id', 'submitTemplateId']
export const cleanTemplate = <T extends Entity>(entity: T): T => { 
  entity = jsonCopyObject(entity)
  templatePropsToRemove.forEach(p => delete entity[p])
  return entity
}

export interface SocialLink {
  type: string
  url: string
}


export const getCustomQuestionsByEntity = (event: JoiEvent, entityTypeId: string, type: CoreEntityType) => {
  let customQuestions: CustomQuestion[] = []
  let liveTypes: Set<string>
  switch (type) {
    case 'performer':
      customQuestions = event.performerQuestions
      liveTypes = getSet(event.performerTypes, 'id')
      break
    case 'exhibitor':
      customQuestions = event.exhibitorQuestions
      liveTypes = getSet(event.exhibitorTypes, 'id')
      break
    case 'resource':
      customQuestions = event.resourceQuestions
      liveTypes = getSet(event.resourceTypes, 'id')
      break
    case 'attendee':
      customQuestions = event.attendeeQuestions
      liveTypes = getSet(event.attendeeTypes, 'id')
      break
    default:
      console.log("No handling for get custom questions by " + type)
      break
  }
  const filteredQuestions = customQuestions.filter(q => !q.typeIds || 
    !q.typeIds.filter(typeId => liveTypes.has(typeId)).length
    || q.typeIds.includes(entityTypeId))
  // console.log({ customQuestions, filteredQuestions })
  return filteredQuestions

}